import React, { useState, useRef, useEffect } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import {
  getRecaptchaKey,
  sendContactPortalEmail,
} from "../../services/apiContacto";
import { useSearchContext } from "../../context/SearchContext";
import APIError from "../../models/ApiError";
import { validateContactForm } from "../../utils/contactValidation";

const ALERT_TIMEOUT_MS = 5000;
const INITIAL_FIELD_ERRORS = {
  name: "",
  email: "",
  message: "",
};

const hasValidationErrors = (errors) =>
  Object.values(errors).some((errorMessage) => Boolean(errorMessage));

const Contacto = () => {
  const captchaRef = useRef(null);
  const [captchaToken, setCaptchaToken] = useState("");
  const [captchaState, setCaptchaState] = useState("idle");
  const [captchaMessage, setCaptchaMessage] = useState("");
  const [formError, setFormError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [isMailSent, setIsMailSent] = useState(false);

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [fieldErrors, setFieldErrors] = useState(INITIAL_FIELD_ERRORS);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [recaptchaKey, setRecaptchaKey] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const { handleApiError, handleApiResponse } = useSearchContext();

  useEffect(() => {
    fetchRecaptchaKey();
  }, []);

  useEffect(() => {
    if (!captchaMessage) {
      return;
    }

    const timeoutId = setTimeout(() => {
      setCaptchaMessage("");
    }, ALERT_TIMEOUT_MS);

    return () => clearTimeout(timeoutId);
  }, [captchaMessage]);

  useEffect(() => {
    if (!formError) {
      return;
    }

    const timeoutId = setTimeout(() => {
      setFormError("");
    }, ALERT_TIMEOUT_MS);

    return () => clearTimeout(timeoutId);
  }, [formError]);

  useEffect(() => {
    if (!isMailSent) {
      return;
    }

    const timeoutId = setTimeout(() => {
      setIsMailSent(false);
    }, ALERT_TIMEOUT_MS);

    return () => clearTimeout(timeoutId);
  }, [isMailSent]);

  const fetchRecaptchaKey = async () => {
    setIsLoading(true);
    try {
      const key = await getRecaptchaKey();
      setRecaptchaKey(key?.recaptcha_key);
    } catch (error) {
      if (error instanceof APIError) {
        handleApiError(error, true);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const getFieldValidationClass = (errorMessage, value) => {
    if (!hasSubmitted && !value.trim()) {
      return "";
    }

    return errorMessage ? "is-invalid" : "is-valid";
  };

  const getAriaDescribedBy = (
    descriptionId,
    errorId,
    successId,
    hasError,
    hasSuccess,
  ) => {
    if (hasError) {
      return `${descriptionId} ${errorId}`;
    }

    if (hasSuccess) {
      return `${descriptionId} ${successId}`;
    }

    return descriptionId;
  };

  const resetCaptcha = (nextState = "idle", message = "") => {
    setCaptchaToken("");
    setCaptchaState(nextState);
    setCaptchaMessage(message);
    captchaRef.current?.reset();
  };

  const runValidation = (nextValues) => {
    const nextErrors = validateContactForm(nextValues);
    setFieldErrors(nextErrors);
    return nextErrors;
  };

  const handleNameChange = (event) => {
    const nextName = event.target.value;
    setName(nextName);

    if (hasSubmitted) {
      runValidation({ name: nextName, email, message });
    }
  };

  const handleEmailChange = (event) => {
    const nextEmail = event.target.value;
    setEmail(nextEmail);

    if (hasSubmitted) {
      runValidation({ name, email: nextEmail, message });
    }
  };

  const handleMessageChange = (event) => {
    const nextMessage = event.target.value;
    setMessage(nextMessage);

    if (hasSubmitted) {
      runValidation({ name, email, message: nextMessage });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) {
      return;
    }

    // Activamos validaciones visuales de Obelisco a partir del primer intento de envio.
    setHasSubmitted(true);
    setFormError("");
    setIsMailSent(false);

    const validationErrors = runValidation({ name, email, message });
    if (hasValidationErrors(validationErrors)) {
      setFormError("Revisá los campos marcados antes de enviar");
      return;
    }

    if (!captchaToken) {
      setCaptchaState("missing");
      setCaptchaMessage("Completá el CAPTCHA para continuar");
      return;
    }

    setIsSubmitting(true);
    try {
      // El backend validará este token; frontend sólo exige su presencia.
      const response = await sendContactPortalEmail(
        name,
        email,
        message,
        captchaToken,
      );

      setIsMailSent(true);
      setName("");
      setEmail("");
      setMessage("");
      setFieldErrors(INITIAL_FIELD_ERRORS);
      setHasSubmitted(false);
      setFormError("");
      resetCaptcha();
      handleApiResponse(response, true);
    } catch (err) {
      setIsMailSent(false);
      // resetCaptcha(
      //   "invalid",
      //   "La validación del CAPTCHA falló. Volvé a completarlo e intentá de nuevo.",
      // );

      handleApiError(err, true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCaptchaChange = (value) => {
    setCaptchaToken(value || "");
    if (value) {
      setCaptchaState("valid");
      setCaptchaMessage("");
      setFormError("");
    } else {
      setCaptchaState("missing");
      setCaptchaMessage("Completá el CAPTCHA para continuar");
    }
  };

  const handleCaptchaExpired = () => {
    setCaptchaToken("");
    setCaptchaState("expired");
    setCaptchaMessage(
      "El CAPTCHA expiró. Por favor completalo nuevamente antes de enviar",
    );
  };

  const handleCaptchaError = () => {
    setCaptchaToken("");
    setCaptchaState("failed");
    setCaptchaMessage(
      "No se pudo verificar el CAPTCHA. Intentá recargarlo y completar nuevamente",
    );
  };

  const showCaptchaAlert =
    (captchaState === "missing" ||
      captchaState === "expired" ||
      captchaState === "failed" ||
      captchaState === "invalid") &&
    captchaMessage;

  const isEmailValid = !fieldErrors.email && email.trim().length > 0;
  const isMessageValid = !fieldErrors.message && message.trim().length > 0;

  const nameHasError = hasSubmitted && Boolean(fieldErrors.name);
  const emailHasError = hasSubmitted && Boolean(fieldErrors.email);
  const messageHasError = hasSubmitted && Boolean(fieldErrors.message);

  const nameHasSuccess =
    hasSubmitted && !fieldErrors.name && name.trim().length > 0;
  const emailHasSuccess = hasSubmitted && isEmailValid;
  const messageHasSuccess = hasSubmitted && isMessageValid;

  const isSubmitDisabled = isSubmitting || isLoading;

  return (
    <div className="container" style={{ padding: 30 }}>
      <div className="card  p-5">
        <div className="row">
          <div className="col-md-6">
            <h2>Contacto</h2>
            <p className="card-text pt-4">
              Podés hacernos llegar tus comentarios, inquietudes y sugerencias
              sobre IDECABA. Por favor, dejanos una dirección de correo válida
              para que podamos responderte. ¡Gracias!
            </p>
          </div>
          <div className="col-md-6">
            <div className="card-body">
              <form onSubmit={handleSubmit} noValidate>
                <div className="mb-3">
                  <div className="form-label-container">
                    <label htmlFor="name" className="form-label">
                      Nombre
                    </label>
                    <span className="badge-forms badge-required-forms">
                      Requerido
                    </span>
                  </div>
                  <input
                    type="text"
                    className={`form-control ${getFieldValidationClass(fieldErrors.name, name)}`}
                    id="name"
                    name="name"
                    placeholder="Ingresa tu nombre"
                    value={name}
                    onChange={handleNameChange}
                    aria-required="true"
                    aria-invalid={nameHasError}
                    aria-describedby={getAriaDescribedBy(
                      "name-help",
                      "name-error",
                      "name-success",
                      nameHasError,
                      nameHasSuccess,
                    )}
                  />
                  <p id="name-help" className="form-label-description">
                    Escribí tu nombre completo para que podamos identificar tu
                    consulta.
                  </p>
                  {nameHasError ? (
                    <div
                      id="name-error"
                      className="invalid-feedback"
                      style={{ display: "block" }}
                    >
                      <p>{fieldErrors.name}</p>
                    </div>
                  ) : null}
                  {nameHasSuccess ? (
                    <div
                      id="name-success"
                      className="valid-feedback"
                      style={{ display: "block" }}
                    >
                      <p>Nombre válido.</p>
                    </div>
                  ) : null}
                </div>
                <div className="mb-4">
                  <div className="form-label-container">
                    <label htmlFor="email" className="form-label">
                      Correo eléctronico
                    </label>
                    <span className="badge-forms badge-required-forms">
                      Requerido
                    </span>
                  </div>
                  <input
                    type="email"
                    className={`form-control ${getFieldValidationClass(fieldErrors.email, email)}`}
                    id="email"
                    name="email"
                    placeholder="ejemplo@ejemplo.com"
                    value={email}
                    onChange={handleEmailChange}
                    // aria-required="true"
                    aria-invalid={emailHasError}
                    aria-describedby={getAriaDescribedBy(
                      "email-help",
                      "email-error",
                      "email-success",
                      emailHasError,
                      emailHasSuccess,
                    )}
                  />
                  <p id="email-help" className="form-label-description">
                    Usá un correo válido para recibir nuestra respuesta.
                  </p>
                  {emailHasError ? (
                    <div
                      id="email-error"
                      className="invalid-feedback"
                      style={{ display: "block" }}
                    >
                      <p>{fieldErrors.email}</p>
                    </div>
                  ) : null}
                  {emailHasSuccess ? (
                    <div
                      id="email-success"
                      className="valid-feedback"
                      style={{ display: "block" }}
                    >
                      <p>Correo válido.</p>
                    </div>
                  ) : null}
                </div>
                <div className="mb-4">
                  <div className="form-label-container">
                    <label htmlFor="message" className="form-label">
                      Mensaje
                    </label>
                    <span className="badge-forms badge-required-forms">
                      Requerido
                    </span>
                    <span className="form-label-number">{`${message.length}/1000`}</span>
                  </div>
                  <textarea
                    className={`form-control ${getFieldValidationClass(fieldErrors.message, message)}`}
                    id="message"
                    name="message"
                    rows="4"
                    placeholder="Escribe tu mensaje"
                    value={message}
                    onChange={handleMessageChange}
                    maxLength={1000}
                    aria-required="true"
                    aria-invalid={messageHasError}
                    aria-describedby={getAriaDescribedBy(
                      "message-help",
                      "message-error",
                      "message-success",
                      messageHasError,
                      messageHasSuccess,
                    )}
                  />
                  <p id="message-help" className="form-label-description">
                    Contanos tu consulta, comentario o sugerencia.
                  </p>
                  {messageHasError ? (
                    <div
                      id="message-error"
                      className="invalid-feedback"
                      style={{ display: "block" }}
                    >
                      <p>{fieldErrors.message}</p>
                    </div>
                  ) : null}
                  {messageHasSuccess ? (
                    <div
                      id="message-success"
                      className="valid-feedback"
                      style={{ display: "block" }}
                    >
                      <p>Mensaje cargado correctamente.</p>
                    </div>
                  ) : null}
                </div>
                <div
                  className="mb-4 form-input-group"
                  aria-describedby="captcha-help"
                >
                  <div className="form-label-container">
                    <span className="form-label">Verificación CAPTCHA</span>
                    <span className="badge-forms badge-required-forms">
                      Requerido
                    </span>
                  </div>
                  {!isLoading && recaptchaKey ? (
                    <ReCAPTCHA
                      ref={captchaRef}
                      sitekey={recaptchaKey}
                      onChange={handleCaptchaChange}
                      onExpired={handleCaptchaExpired}
                      onErrored={handleCaptchaError}
                      id="reCAPTCHA"
                    />
                  ) : null}
                  {showCaptchaAlert ? (
                    <div
                      className="invalid-feedback"
                      style={{ display: "block" }}
                    >
                      <p>{captchaMessage}</p>
                    </div>
                  ) : null}
                  {/* {captchaState === "valid" && captchaToken ? (
                    <div
                      className="valid-feedback"
                      style={{ display: "block" }}
                    >
                      <p>CAPTCHA verificado correctamente.</p>
                    </div>
                  ) : null} */}
                </div>

                {/* {formError ? (
                  <div
                    className="invalid-feedback"
                    style={{ display: "block" }}
                  >
                    <p>{formError}</p>
                  </div>
                ) : null} */}

                {/* {isMailSent ? (
                  <div className="valid-feedback" style={{ display: "block" }}>
                    <p>Correo enviado con exito.</p>
                  </div>
                ) : null} */}
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={isSubmitDisabled}
                >
                  {isSubmitting ? "Enviando..." : "Enviar"}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contacto;
